import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setCookie, deleteCookie } from '../../utils';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../api';
import { verifyAccessToken, loginAsAdminFunc, updateWeekAndDay } from '../../redux/slices/protectSlice';

export const loginCustomer = createAsyncThunk(
  'customerAuth/loginCustomer',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/login/customer`, credentials);
      const data = response.data;

      localStorage.setItem('firstName', data.data.user.first_name);
      localStorage.setItem('lastName', data.data.user.last_name);
      localStorage.setItem('email', data.data.user.email);
      localStorage.setItem('accountType', data.data.user.account_type);

      dispatch(verifyAccessToken());

      setCookie('accessToken', data.data.access_token, 30);
      deleteCookie('adminaccessToken');

      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Login failed');
    }
  }
);

export const sendOtp = createAsyncThunk(
  'customerAuth/sendOtp',
  async (email, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/send-otp`, { email });
      localStorage.setItem('otpEmail', email);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to send OTP');
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'customerAuth/verifyOtp',
  async (otp, { rejectWithValue }) => {
    try {
      const email = localStorage.getItem('otpEmail');
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/verify-otp`, { email, otp });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to verify OTP');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'customerAuth/resetPassword',
  async (newPassword, { rejectWithValue }) => {
    try {
        const email = localStorage.getItem('otpEmail');
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/reset-password`, { email, newPassword });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to reset password');
    }
  }
);

const initialState = {
  user: null,
  status: 'idle',
  error: null,
  message: null,
};

// Create slice for customer authentication
const customerAuthSlice = createSlice({
  name: 'customer_auth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearMessage(state) {
      state.message = null;
    },
    setLoggedInUser(state, action) {
      state.user = action.payload;
    },
    logout(state) {
      state.user = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginCustomer.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginCustomer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.data.user;
      })
      .addCase(loginCustomer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Error logging in';
      })
      .addCase(sendOtp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Error sending OTP';
      })
      .addCase(verifyOtp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Error verifying OTP';
      })
      .addCase(resetPassword.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Error resetting password';
      });
  },
});
 
export default customerAuthSlice.reducer;